import { h } from '../utils/dom.ts';
import { getLocale, t } from '../i18n/index.ts';
import { heroArt, iconEl } from './illustrations.ts';
import { tenantAsset } from '../services/tenant.ts';
import { formatDateKey } from '../utils/format.ts';
import { hhmm, hoursOn, openStatus, WEEK } from '../utils/hours.ts';
import { addDays, dateKeyInTz, weekdayOf } from '../utils/time.ts';
import type { Business } from '../utils/validation.ts';

/** Big branded header of a shop page: name, live open/closed status, CTAs, artwork. */
export function shopHero(business: Business, onBook: () => void): HTMLElement {
  const status = openStatus(business, new Date());
  const statusText = status.open
    ? t('hero.openNow', { time: hhmm(status.closesAt) })
    : !status.next
      ? ''
      : status.next.inDays === 0
        ? t('hero.opensToday', { time: hhmm(status.next.opensAt) })
        : status.next.inDays === 1
          ? t('hero.opensTomorrow', { time: hhmm(status.next.opensAt) })
          : t('hero.opensOn', { day: formatDateKey(status.next.date, getLocale(), { weekday: 'long' }), time: hhmm(status.next.opensAt) });

  return h('section', { class: 'hero', 'aria-labelledby': 'shop-name' },
    h('div', { class: 'hero-pattern', 'aria-hidden': 'true' }),
    h('div', { class: 'hero-body' },
      business.logo && h('img', { src: tenantAsset(business.slug, business.logo), alt: '', width: 64, height: 64, class: 'hero-logo' }),
      h('p', { class: 'hero-kicker' }, t('hero.kicker', { city: business.location.city })),
      h('h1', { class: 'hero-title', id: 'shop-name' }, business.name),
      statusText && h('p', { class: `hero-status ${status.open ? 'is-open' : 'is-closed'}` }, h('span', { class: 'dot', 'aria-hidden': 'true' }), statusText),
      h('div', { class: 'hero-actions' },
        h('button', { type: 'button', class: 'btn btn-hero', onClick: onBook }, t('hero.book'), iconEl('arrowDown', 18)),
        h('a', { href: `tel:${business.contact.phone.replace(/[^\d+]/g, '')}`, class: 'btn btn-hero-ghost' }, iconEl('phone', 18), t('hero.call')),
      ),
    ),
    h('div', { class: 'hero-art-wrap', 'aria-hidden': 'true' }, heroArt()),
  );
}

/** Address · opening hours · contact, as three compact cards. */
export function shopInfo(business: Business): HTMLElement {
  const locale = getLocale();
  const today = dateKeyInTz(new Date(), business.timezone);
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${business.name}, ${business.location.address}`)}`;
  const todayHours = hoursOn(business, today);

  // A reference week (Mon..Sun) for localized weekday names.
  const monday = (() => {
    let d = today;
    while (weekdayOf(d) !== 'mon') d = addDays(d, -1);
    return d;
  })();
  const week = h('ul', { class: 'hours-list' }, WEEK.map((day, i) => {
    const date = addDays(monday, i);
    const raw = business.business_hours[day];
    return h('li', { class: date === today ? 'is-today' : undefined },
      h('span', null, formatDateKey(date, locale, { weekday: 'long' })),
      h('span', null, raw === 'closed' ? t('book.closed') : raw.replace('-', ' – ')),
    );
  }));

  const card = (iconName: Parameters<typeof iconEl>[0], title: string, ...body: (Node | string | false | null | undefined)[]) =>
    h('div', { class: 'info-card' }, h('span', { class: 'info-icon' }, iconEl(iconName, 20)), h('div', null, h('strong', null, title), ...body));

  return h('div', { class: 'info-grid' },
    card('pin', t('info.address'),
      h('span', null, business.location.address),
      h('a', { href: mapsUrl, target: '_blank', rel: 'noopener noreferrer' }, t('info.route'), ' →'),
    ),
    card('clock', t('info.hours'),
      h('span', null, `${t('info.today')}: ${todayHours ? `${hhmm(todayHours[0])} – ${hhmm(todayHours[1])}` : t('book.closed')}`),
      h('details', null, h('summary', null, t('info.hours')), week),
    ),
    card('phone', t('info.contact'),
      h('a', { href: `tel:${business.contact.phone.replace(/[^\d+]/g, '')}` }, business.contact.phone),
      h('a', { href: `mailto:${business.contact.email}` }, business.contact.email),
    ),
  );
}

/** Animated red-white-blue barber pole band, in the shop's colours. */
export function poleBand(): HTMLElement {
  return h('div', { class: 'pole-band', 'aria-hidden': 'true' });
}
